import React, { useState, useEffect, useRef, useCallback } from "react";
import { Row, Col, Card, Button, Image } from "antd";
import CustomerMainLayout from "../CustomerMainLayout";
import DottedLoader from "../../DottedLoader";
import "./InspirationLibrary.scss";
import { ReactComponent as ImgOutline } from '../../../../assets/images/black-large-img-outline.svg';
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import { getBaseURL } from "../../../../environments/env";

const CUSTOMER_USERNAME = localStorage.getItem("username");
const COMPANY_ID = localStorage.getItem("company_id");

const InspirationLibrary = () => {
  const [images, setImages] = useState([]);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const galleryRef = useRef();
  const pageRef = useRef(1);
  const pageSize = 20;

  const fetchInspirationImages = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        action: 'get_all_inspirations',
        username: CUSTOMER_USERNAME,
        company_id: COMPANY_ID
      };
      
      const response = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);
      
      if (response.data && response.data.result === 'success' && Array.isArray(response.data.data)) {
        const baseURL = getBaseURL();
        const processedImages = response.data.data.map((image, index) => {
          const baseImageUrl = baseURL + image.image_uri;
          
          return {
            src: baseImageUrl,
            thumbnail: baseImageUrl,
            width: image.width,
            height: image.height,
            depth: image.depth,
            id: image.id || index,
            title: `Inspiration ${image.id || index + 1}`,
            created_on: image.created_on,
            image_uri: image.image_uri,
            ...image
          };
        });
        setImages(processedImages);
      } else {
        // Handle empty or unexpected response
        setImages([]);
      }
    } catch (error) {
      console.error("Error fetching inspiration images:", error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInspirationImages();
  }, []);

  const loadMoreImages = useCallback(() => {
    if (!images || images.length === 0) return;
    
    const startIndex = (pageRef.current - 1) * pageSize;
    const newImages = images.slice(startIndex, startIndex + pageSize);

    if (newImages.length > 0) {
      setDisplayedImages((prev) => [...prev, ...newImages]);
      pageRef.current += 1;
    }
  }, [images, pageSize]);

  useEffect(() => {
    setDisplayedImages([]);
    pageRef.current = 1;
    
    if (images && images.length > 0) {
      loadMoreImages();
    }
  }, [images, loadMoreImages]);

  const handleScroll = useCallback(() => {
    if (!galleryRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = galleryRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      loadMoreImages();
    }
  }, [loadMoreImages]);

  const handleImageClick = (index) => {
    const imageData = displayedImages[index];
    if (imageData) {
      setSelectedImage(imageData);
      // Use setTimeout to ensure Image component is rendered before showing preview
      setTimeout(() => {
        setIsModalVisible(true);
      }, 0);
    }
  };


  const CustomThumbnail = ({ item, onClick }) => {
    return (
      <Col span={5}
        style={{ 
          cursor: 'pointer',
        }}
      >
        <Card onClick={onClick} style={{ border: '1px solid #e3e3e3', borderRadius: '4px' }}>
        <img
          src={item.thumbnail || item.src}
          alt={item.title || "Inspiration image"}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
        </Card>
      </Col>
    );
  };

  if (loading) {
    return (
      <CustomerMainLayout selected="36">
        <div>
          <DottedLoader />
        </div>
      </CustomerMainLayout>
    );
  }

  if (!displayedImages.length) {
    return (
      <CustomerMainLayout selected="36">
        <div>
          <Row justify="center" align="middle" style={{ marginTop: "5%" }}>
            <Col span={24} style={{ textAlign: "center" }}>
              <ImgOutline />
              <span className="manrope f-20 mt-24" style={{ display: "block" }}>
                No inspiration images in your library yet
              </span>
              <div className="mt-16" style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                <Button 
                  type="primary"
                  className="browse-previews-btn"
                >
                  Upload Inspiration Images
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </CustomerMainLayout>
    );
  }

  return (
    <CustomerMainLayout selected="36">
      <div style={{ padding: "20px" }}>
        <Row style={{ marginBottom: "20px" }}>
          <Col span={24}>
            <h1 className="manrope f-24 w-600">Inspirations</h1>
            <p className="manrope f-14" style={{ color: "#8c8c8c", marginTop: "8px" }}>
              Browse and manage your inspiration images
            </p>
          </Col>
        </Row>

        <Row
          ref={galleryRef}
          onScroll={handleScroll}
          style={{ overflowY: "auto", display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: `${window.innerHeight * 0.75}px` }}
        >
            {displayedImages.map((item, index) => {
                return <CustomThumbnail key={index} onClick={() => handleImageClick(index)} item={item} />
            })}
        </Row>

        {selectedImage && (
          <Image
            style={{ display: 'none' }}
            src={selectedImage.src}
            preview={{
              visible: isModalVisible,
              onVisibleChange: (visible) => {
                setIsModalVisible(visible);
                if (!visible) {
                  setSelectedImage(null);
                }
              },
              getContainer: () => document.body
            }}
          />
        )}
      </div>
    </CustomerMainLayout>
  );
};

export default InspirationLibrary;

